/**
 * The data plane list for the rail picker (plan 2026-10-14-console-dp-picker, D6 and ruling R2).
 *
 * The rail chip on a data plane page offers a switch to another data plane. That needs the list of
 * data planes the principal can read, and no other shell state holds it. The server reads live
 * status per row for `GET /v1/admin/data-planes`, so the request is expensive. This provider sends it
 * only when it must:
 *
 * 1. The provider sends no request until a consumer asks with `enabled` true.
 * 2. The provider sends no request while the permission snapshot lacks `cp:dp:read`.
 * 3. The provider keeps the answer for the operator identity that asked. A switch between data
 *    planes, a second consumer and a remount send no second request.
 * 4. After a 403, the provider gives `null` for the session. After any other failure, the provider
 *    sends one retry on the next ask: the next time a data plane page opens.
 *
 * The snapshot check saves a request and authorizes nothing. The server's
 * `@RequirePermission('cp:dp:read')` is the gate.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import { useHasPermission } from '@zion/sdk-react';
import { DP_READ_PERMISSION, listDataPlanes, type DataPlaneSummary } from '../api/dataPlanes.api';
import { ApiError } from '../platform/http/client';
import { hasPlacement } from '../panes/retained-project';

/** What the provider carries to {@link useDataPlaneChoices}. */
interface DataPlaneChoicesValue {
  /** The rows the current operator may see, or `null` when not known, failed or forbidden. */
  readonly choices: readonly DataPlaneSummary[] | null;
  /** Asks for the list for the current operator. Identity-stable per operator. */
  readonly ask: () => void;
}

/** Reads per operator at most: the first read, and one retry after a failure that is not a 403. */
const MAX_READS = 2;

/** How one read ended. */
type ReadOutcome =
  | { readonly kind: 'rows'; readonly rows: readonly DataPlaneSummary[] }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'failed' };

/** The last read that ended for {@link ReadState.operatorId}. */
interface SettledRead {
  /** 1 for the first read, 2 for the retry. */
  readonly read: number;
  readonly outcome: ReadOutcome;
  /** The ask count when the read ended. A later ask is a new open of a data plane page. */
  readonly asksBefore: number;
}

/** The asks and the last ended read of one operator. An ask for a new operator starts over. */
interface ReadState {
  readonly operatorId: string | null;
  readonly asks: number;
  readonly settled: SettledRead | null;
}

type ReadAction =
  | { readonly type: 'ask'; readonly operatorId: string | null }
  | { readonly type: 'settle'; readonly read: number; readonly outcome: ReadOutcome };

const INITIAL_STATE: ReadState = { operatorId: null, asks: 0, settled: null };

function reduceReads(state: ReadState, action: ReadAction): ReadState {
  if (action.type === 'ask') {
    return state.operatorId === action.operatorId
      ? { ...state, asks: state.asks + 1 }
      : { operatorId: action.operatorId, asks: 1, settled: null };
  }
  // The effect drops a late answer, so a settle belongs to the operator in the state.
  return { ...state, settled: { read: action.read, outcome: action.outcome, asksBefore: state.asks } };
}

/** The read the operator needs now: 0 for none, else the number of the read to send. */
function readToSend(asks: number, settled: SettledRead | null): number {
  if (asks === 0) return 0;
  if (settled === null) return 1;
  const retry =
    settled.outcome.kind === 'failed' && settled.read < MAX_READS && asks > settled.asksBefore;
  return retry ? settled.read + 1 : 0;
}

const DataPlaneChoicesCtx = createContext<DataPlaneChoicesValue | null>(null);

export interface DataPlaneChoicesProviderProps {
  /**
   * The signed-in operator. The provider keeps one list per value and drops a list read for
   * another value on the render where the value changes.
   */
  readonly operatorId: string | null;
  readonly children: ReactNode;
}

/**
 * Holds the lazy data plane list for the authenticated subtree.
 *
 * Mount it inside `PermissionProvider`, because it reads `cp:dp:read` from the snapshot, and above
 * `AuthenticatedShell`, because `AppHostFrame` is the consumer. It sends
 * `GET /v1/admin/data-planes?status=in-service` only after a consumer asks. Per operator identity:
 *
 * - rows: kept, and no further request.
 * - a 403: `null`, and no further request for the session.
 * - any other error status or a network failure: `null`, and one retry on the next ask. After the
 *   retry fails, no further request.
 * - an answer that lands after the operator, the permission or the provider changed: dropped.
 *
 * A snapshot without `cp:dp:read` gives `null` and no request.
 */
export function DataPlaneChoicesProvider({
  operatorId,
  children,
}: DataPlaneChoicesProviderProps): React.JSX.Element {
  const canRead = useHasPermission(DP_READ_PERMISSION);
  const [state, dispatch] = useReducer(reduceReads, INITIAL_STATE);

  const ask = useCallback(() => {
    dispatch({ type: 'ask', operatorId });
  }, [operatorId]);

  // Derived during render, so a new operator sees nothing of the old one at once.
  const mine = state.operatorId === operatorId ? state : null;
  const settled = mine === null ? null : mine.settled;
  const read = mine === null ? 0 : readToSend(mine.asks, settled);

  useEffect(() => {
    if (!canRead || read === 0) return undefined;
    let live = true;
    listDataPlanes().then(
      (rows) => {
        // A row with no placement has no detail page to switch to (R-012), so the picker drops it.
        if (live) dispatch({ type: 'settle', read, outcome: { kind: 'rows', rows: rows.filter(hasPlacement) } });
      },
      (error: unknown) => {
        const kind = error instanceof ApiError && error.status === 403 ? 'forbidden' : 'failed';
        if (live) dispatch({ type: 'settle', read, outcome: { kind } });
      },
    );
    return () => {
      live = false;
    };
  }, [canRead, operatorId, read]);

  // A lost permission hides the list at once. A regained one shows it again with no request.
  const choices =
    canRead && settled !== null && settled.outcome.kind === 'rows' ? settled.outcome.rows : null;
  const value = useMemo<DataPlaneChoicesValue>(() => ({ choices, ask }), [choices, ask]);
  return <DataPlaneChoicesCtx value={value}>{children}</DataPlaneChoicesCtx>;
}

/**
 * The data planes the operator may switch to, for the rail picker in `AppHostFrame`.
 *
 * - `enabled` false: returns `null` and asks for nothing. `AppHostFrame` passes true only while a
 *   data plane detail page is open.
 * - `enabled` true: asks the provider once per change to true, then returns `null` until the rows
 *   arrive, and the rows after. A forbidden read stays `null`. A failed read stays `null` until the
 *   next change to true, which retries once.
 * - No provider: returns `null`, so a tree without the provider renders a static chip.
 *
 * The rows are the server's in-service set in the server's order, including the current data plane.
 * The provider removes each row with no placement (`hasPlacement` false, R-012 of plan
 * 2026-10-22-retained-tenant-projects) before it stores the rows.
 */
export function useDataPlaneChoices(enabled: boolean): readonly DataPlaneSummary[] | null {
  const ctx = useContext(DataPlaneChoicesCtx);
  const ask = ctx === null ? null : ctx.ask;
  useEffect(() => {
    if (enabled && ask !== null) ask();
  }, [enabled, ask]);
  return enabled && ctx !== null ? ctx.choices : null;
}
